import { useEffect, useState, useCallback } from 'react';
import { Box, Typography, Grid, Paper, CircularProgress, Alert, Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Chip, TextField, InputAdornment } from '@mui/material';
import { useTranslation } from 'react-i18next';
import MonthYearSelector from '../../components/Reports/Sales/MonthYearSelector';
import { getSummaryInvoices, recalculateTaxSummary, closeTaxSummaryMonth, adjustTaxSummary } from '../../services/ElectronicBillingService';
import SnackbarUtils from '../../utils/SnackbarUtils';
import { hasStoredPermission } from '../../utils/dashboardPermissions';
import dayjs from 'dayjs';
import { formatMoney } from '../../utils/currency';

const SummaryInvoicesTable = ({ selectedRut, readOnly = false, embedded = false }) => {
  const { t } = useTranslation();
  const [month, setMonth] = useState(dayjs().month() + 1);
  const [year, setYear] = useState(dayjs().year());
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [adjustDialogOpen, setAdjustDialogOpen] = useState(false);
  const [adjusting, setAdjusting] = useState(false);
  const [adjustForm, setAdjustForm] = useState({
    ivaVenta: '',
    ivaCompra: '',
    saldoMesAnterior: '',
    resguardo: '',
  });

  const isCurrentMonth = month === dayjs().month() + 1 && year === dayjs().year();
  const isClosed = summary?.status === 'CLOSED';
  const canAdjustSummary = !readOnly && hasStoredPermission('CREATE_CUSTOM_INVOICES');

  const fetchSummary = useCallback(async () => {
    if (selectedRut === null) return;
    setLoading(true);
    setLoadError(null);
    try {
      const data = await getSummaryInvoices({ month, year, rut: selectedRut });
      setSummary(data);
    } catch (err) {
      console.error('Error fetching summary invoices', err);
      setSummary(null);
      setLoadError(err?.response?.data?.error_message || err?.message || t('tax_summary_refresh_error'));
    } finally {
      setLoading(false);
    }
  }, [month, year, selectedRut, t]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const handleRecalculateSummary = async () => {
    if (selectedRut === null) return;
    setRefreshing(true);
    setLoadError(null);
    try {
      const data = await recalculateTaxSummary({ year, month, rut: selectedRut });
      setSummary(data);
      SnackbarUtils.success(t('tax_summary_recalculated'));
    } catch (err) {
      console.error('Error recalculating summary invoices', err);
      setLoadError(err?.response?.data?.error_message || err?.message || t('tax_summary_refresh_error'));
      SnackbarUtils.error(t('tax_summary_refresh_error'));
    } finally {
      setRefreshing(false);
    }
  };

  const handleCloseMonthClick = () => setCloseDialogOpen(true);
  const handleCloseDialogCancel = () => setCloseDialogOpen(false);
  const handleCloseDialogConfirm = async () => {
    setClosing(true);
    try {
      await closeTaxSummaryMonth({ year, month, rut: selectedRut });
      SnackbarUtils.success(t('tax_summary_month_closed'));
      setCloseDialogOpen(false);
      setSummary((prev) => (prev ? { ...prev, status: 'CLOSED' } : null));
    } catch (err) {
      const msg = err?.response?.data?.error_message || err?.message || t('error_generic');
      SnackbarUtils.error(msg);
    } finally {
      setClosing(false);
    }
  };

  const handleAdjustClick = () => {
    if (!summary || isClosed) return;
    setAdjustForm({
      ivaVenta: String(summary.ivaVenta ?? 0),
      ivaCompra: String(summary.ivaCompra ?? 0),
      saldoMesAnterior: String(summary.saldoMesAnterior ?? 0),
      resguardo: String(summary.resguardo ?? 0),
    });
    setAdjustDialogOpen(true);
  };

  const handleAdjustDialogCancel = () => {
    if (!adjusting) setAdjustDialogOpen(false);
  };

  const handleAdjustFormChange = (field) => (event) => {
    setAdjustForm((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const parseAmount = (value) => {
    const normalized = String(value).replace(',', '.');
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : NaN;
  };

  const handleAdjustDialogConfirm = async () => {
    const ivaVenta = parseAmount(adjustForm.ivaVenta);
    const ivaCompra = parseAmount(adjustForm.ivaCompra);
    const saldoMesAnterior = parseAmount(adjustForm.saldoMesAnterior || 0);
    const resguardo = parseAmount(adjustForm.resguardo || 0);

    if (
      ivaVenta < 0 ||
      ivaCompra < 0 ||
      saldoMesAnterior < 0 ||
      resguardo < 0 ||
      [ivaVenta, ivaCompra, saldoMesAnterior, resguardo].some(Number.isNaN)
    ) {
      SnackbarUtils.error(t('invalid_amount'));
      return;
    }

    setAdjusting(true);
    try {
      const data = await adjustTaxSummary({
        year,
        month,
        rut: selectedRut,
        ivaVenta,
        ivaCompra,
        saldoMesAnterior,
        resguardo,
        cantidadEmitidas: summary?.cantidadEmitidas,
        cantidadRecibidas: summary?.cantidadRecibidas,
      });
      setSummary(data);
      setAdjustDialogOpen(false);
      SnackbarUtils.success(t('tax_summary_adjust_saved'));
    } catch (err) {
      const msg = err?.response?.data?.error_message || err?.message || t('error_generic');
      SnackbarUtils.error(msg);
    } finally {
      setAdjusting(false);
    }
  };

  const Root = embedded ? Box : Paper;
  const rootProps = embedded
    ? { sx: { mb: 2 } }
    : { sx: { padding: 2, mb: 2 } };

  return (
    <Root {...rootProps}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {isCurrentMonth && (
            <Alert severity="info" sx={{ mb: 0 }}>
              {t('tax_summary_month_not_closed')}
            </Alert>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleRecalculateSummary}
            disabled={loading || refreshing || selectedRut === null}
          >
            {refreshing ? t('loading') || '...' : t('tax_summary_refresh_now')}
          </Button>
          <Box sx={{ width: 220 }}>
          <MonthYearSelector month={month} year={year} setMonth={setMonth} setYear={setYear} />
          </Box>
        </Box>
      </Box>

      {loadError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('tax_summary_refresh_error')}
        </Alert>
      ) : loading ? (
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 180 }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }} color="text.secondary">
            {t('tax_summary_calculating')}
          </Typography>
        </Box>
      ) : summary ? (
        <>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Paper elevation={2} sx={{ p: 2 }}>
                <Typography variant="subtitle2">{t('iva_sale')}</Typography>
                <Typography variant="h6">{formatMoney(summary.ivaVenta)}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper elevation={2} sx={{ p: 2 }}>
                <Typography variant="subtitle2">{t('iva_purchase')}</Typography>
                <Typography variant="h6">{formatMoney(summary.ivaCompra)}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper elevation={2} sx={{ p: 2 }}>
                <Typography variant="subtitle2">{t('resguardo')}</Typography>
                <Typography variant="h6">{formatMoney(Number(summary.resguardo ?? 0))}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper elevation={2} sx={{ p: 2 }}>
                <Typography variant="subtitle2">
                  {summary.saldo > 0 ? t('saldo_a_favor') : t('saldo')}
                </Typography>
                <Typography variant="h6">{formatMoney(summary.saldo)}</Typography>
                {summary.saldo > 0 && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    {t('saldo_a_favor_help')}
                  </Typography>
                )}
              </Paper>
            </Grid>
            {summary.saldoMesAnterior != null && (
              <Grid item xs={12} md={4}>
                <Paper elevation={2} sx={{ p: 2 }}>
                  <Typography variant="subtitle2">{t('saldo_mes_anterior')}</Typography>
                  <Typography variant="h6">{formatMoney(Number(summary.saldoMesAnterior))}</Typography>
                </Paper>
              </Grid>
            )}
            <Grid item xs={12} md={4}>
              <Paper elevation={2} sx={{ p: 2 }}>
                <Typography variant="subtitle2">{t('iva_to_pay')}</Typography>
                <Typography variant="h6">{formatMoney(summary.ivaAPagar)}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper elevation={2} sx={{ p: 2 }}>
                <Typography variant="subtitle2">{t('emitted_invoice_count')}</Typography>
                <Typography variant="h6">{summary.cantidadEmitidas}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper elevation={2} sx={{ p: 2 }}>
                <Typography variant="subtitle2">{t('received_invoice_count')}</Typography>
                <Typography variant="h6">{summary.cantidadRecibidas}</Typography>
              </Paper>
            </Grid>
          </Grid>

          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            {isClosed && <Chip label={t('tax_summary_month_closed')} color="success" size="small" />}
            {canAdjustSummary && !isClosed && (
              <Button
                variant="outlined"
                color="primary"
                size="small"
                onClick={handleAdjustClick}
              >
                {t('tax_summary_adjust_values')}
              </Button>
            )}
            {canAdjustSummary && !isCurrentMonth && !isClosed && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={handleCloseMonthClick}
              >
                {t('tax_summary_close_month')}
              </Button>
            )}
          </Box>
        </>
      ) : (
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 3, textAlign: 'center', bgcolor: 'background.paper' }}>
          <Typography color="text.secondary" sx={{ mb: 1 }}>
            {t('tax_summary_no_data')}
          </Typography>
          <Button variant="outlined" size="small" onClick={handleRecalculateSummary} disabled={loading || refreshing || selectedRut === null}>
            {t('tax_summary_refresh_now')}
          </Button>
        </Box>
      )}

      <Dialog open={closeDialogOpen} onClose={handleCloseDialogCancel}>
        <DialogTitle>{t('tax_summary_close_month')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('tax_summary_close_confirm')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogCancel} disabled={closing}>
            {t('cancel')}
          </Button>
          <Button onClick={handleCloseDialogConfirm} color="primary" variant="contained" disabled={closing}>
            {closing ? t('loading') || '...' : t('confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={adjustDialogOpen} onClose={handleAdjustDialogCancel} maxWidth="sm" fullWidth>
        <DialogTitle>{t('tax_summary_adjust_title')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t('tax_summary_adjust_description')}
          </DialogContentText>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField
                label={t('iva_sale')}
                value={adjustForm.ivaVenta}
                onChange={handleAdjustFormChange('ivaVenta')}
                type="number"
                fullWidth
                inputProps={{ min: 0, step: '0.01' }}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label={t('iva_purchase')}
                value={adjustForm.ivaCompra}
                onChange={handleAdjustFormChange('ivaCompra')}
                type="number"
                fullWidth
                inputProps={{ min: 0, step: '0.01' }}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label={t('saldo_mes_anterior')}
                value={adjustForm.saldoMesAnterior}
                onChange={handleAdjustFormChange('saldoMesAnterior')}
                type="number"
                fullWidth
                inputProps={{ min: 0, step: '0.01' }}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label={t('resguardo')}
                value={adjustForm.resguardo}
                onChange={handleAdjustFormChange('resguardo')}
                type="number"
                fullWidth
                inputProps={{ min: 0, step: '0.01' }}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAdjustDialogCancel} disabled={adjusting}>
            {t('cancel')}
          </Button>
          <Button onClick={handleAdjustDialogConfirm} color="primary" variant="contained" disabled={adjusting}>
            {adjusting ? t('loading') || '...' : t('confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Root>
  );
};

export default SummaryInvoicesTable;
